import { MANUAL_PHRASE_SPLIT_KEY, RECORDING_MODE_CONTINUOUS, type RecordingMode } from '@/constants/practiceConstants'

/** What a VAD voice-stop event should do during teacher recording. */
export type VoiceStopAction = 'none' | 'advance' | 'stop'

/**
 * Decide what a VAD silence event means during recording.
 * - Manual Mode: the user drives phrase boundaries with the phrase-split key, so VAD silence does nothing.
 * - Re-recording a single segment, or finishing the last segment: stop recording.
 * - Otherwise: advance the highlight to the next segment.
 */
export function voiceStopAction(params: {
  currentSegment: number
  isReplacement: boolean
  manualMode: boolean
  nSegments: number
}): VoiceStopAction {
  const { currentSegment, isReplacement, manualMode, nSegments } = params
  if (manualMode) {
    return 'none'
  }
  if (isReplacement || currentSegment + 1 >= nSegments) {
    return 'stop'
  }
  return 'advance'
}

/** What the manual phrase-split key should do during recording. */
export interface ManualPhraseAction {
  advanceTo: number
  handled: boolean
  stop: boolean
}

/**
 * Decide how to handle a keydown while recording. Only the configured phrase-split key,
 * pressed (not auto-repeated) while recording in Manual Mode, advances a phrase. Advancing
 * past the last segment also stops the recording.
 */
export function manualPhraseAction(params: {
  currentSegment: number
  isRecording: boolean
  key: string
  manualMode: boolean
  nSegments: number
  repeat: boolean
}): ManualPhraseAction {
  const { currentSegment, isRecording, key, manualMode, nSegments, repeat } = params
  if (key !== MANUAL_PHRASE_SPLIT_KEY || repeat || !isRecording || !manualMode) {
    return { advanceTo: currentSegment, handled: false, stop: false }
  }
  const advanceTo = currentSegment + 1
  return { advanceTo, handled: true, stop: advanceTo >= nSegments }
}

/**
 * After a successful upload, decide whether to auto-advance to the next sentence.
 * Continuous mode advances unless the user pressed Stop; Manual Mode does NOT block this
 * (it governs phrase boundaries within a sentence, not sentence-to-sentence flow).
 */
export function shouldAutoAdvanceAfterUpload(params: {
  canProceedToNext: boolean
  manualStop: boolean
  recordingMode: RecordingMode
}): boolean {
  const { canProceedToNext, manualStop, recordingMode } = params
  return !manualStop && recordingMode === RECORDING_MODE_CONTINUOUS && canProceedToNext
}
